import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { messages } from './messages'
import {
  DEFAULT_LOCALE,
  isLocale,
  localeFromPathname,
  localePath as buildLocalePath,
} from './routes'

const LocaleContext = createContext(null)

const STORAGE_KEY = 'wyrd-grove-locale'

function detectBrowserLocale() {
  const langs = navigator.languages?.length
    ? navigator.languages
    : [navigator.language]
  for (const lang of langs) {
    if (lang?.toLowerCase().startsWith('it')) return 'it'
  }
  return DEFAULT_LOCALE
}

export function getPreferredLocale() {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'it' || stored === 'en') return stored
  return detectBrowserLocale()
}

function initialLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  const fromUrl = localeFromPathname(window.location.pathname)
  if (fromUrl) return fromUrl
  return getPreferredLocale()
}

export function LocaleProvider({ children }) {
  const location = useLocation()
  const [locale, setLocaleState] = useState(initialLocale)

  useEffect(() => {
    const fromUrl = localeFromPathname(location.pathname)
    if (fromUrl && fromUrl !== locale) {
      setLocaleState(fromUrl)
      localStorage.setItem(STORAGE_KEY, fromUrl)
    }
  }, [location.pathname, locale])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo(() => {
    const t = messages[locale]
    const pick = (field) => {
      if (!field) return ''
      if (typeof field === 'string') return field
      return field[locale] ?? field.it ?? field.en ?? ''
    }

    const setLocale = (next) => {
      if (!isLocale(next)) return
      setLocaleState(next)
      localStorage.setItem(STORAGE_KEY, next)
    }

    const localePath = (path = '/') => buildLocalePath(locale, path)

    return { locale, setLocale, localePath, t, pick }
  }, [locale])

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  )
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return ctx
}
